// Le site du club (D-140, lot A) : les fonctions de la base (supabase/migrations/20261104400000_site_du_club.sql) et les
// données des blocs vivants, lues par les fonctions publiques existantes (club_public, club_public_agenda…) : jamais de
// nouvelle porte vers des données personnelles.
import { shopPublic, type ShopPublic } from './boutique-api';
import { supabase } from './supabase';
import { clubMatchEntries, clubPublic, clubPublicGroups, clubPublicPhotos, publicAgenda, type PublicClub, type PublicEvent } from './api';
import { publicBureau } from './recus-api';
import type { MatchEntry } from '../domain/entry';
import type { ShopLink, SiteDoc } from '../domain/site';

const db = () => { if (!supabase) throw new Error('Base non configurée'); return supabase; };
async function rpc<T>(name: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await db().rpc(name, args);
  if (error) throw Object.assign(new Error(error.message), { code: error.code, hint: (error as { hint?: string }).hint });
  return data as T;
}

export type SiteVia = 'bureau' | 'fonction';
export interface SiteVersionRef { id: string; n: number; at: string; by: string; doc: SiteDoc }
export interface SiteRecord {
  draft: SiteDoc; rev: number; draft_at: string; draft_by: string;
  status: 'brouillon' | 'en_ligne' | 'retire'; online_since: string | null;
  option: { active: boolean; until: string | null; requested_at: string | null };
  shop: ShopLink[]; created_at: string; created_by: string;
  published: SiteVersionRef | null;
  news: number; photos: number; pdfs: number;
}
export interface SiteState { via: SiteVia; slug: string | null; public: boolean; color2: string | null; tz: string; site: SiteRecord | null }
export const siteState = (club: string) => rpc<SiteState | null>('site_state', { p_club: club });
export const siteAccess = (club: string) => rpc<SiteVia | null>('site_access', { p_club: club });
/** D-142 : l'offre « Site personnalisable » est-elle proposée à ce club (la bêta tant que le lot B n'est pas livré) ? */
export const siteOffered = (club: string) => rpc<boolean | null>('site_offered', { p_club: club });
export interface SiteBrief { exists: boolean; status?: SiteRecord['status']; option?: boolean; dirty?: boolean }
export const siteBrief = (club: string) => rpc<SiteBrief | null>('site_brief', { p_club: club });
export const siteCreate = (club: string, doc: SiteDoc) => rpc<SiteState>('site_create', { p_club: club, p_doc: doc });
export const siteSave = (club: string, doc: SiteDoc, rev: number) => rpc<number>('site_save', { p_club: club, p_doc: doc, p_rev: rev });
export const sitePublish = (club: string, notes: string[]) => rpc<{ id: string; n: number }>('site_publish', { p_club: club, p_notes: notes });
export interface SiteVersion { id: string; n: number; kind: 'creation' | 'publication'; created_at: string; by_label: string; notes: string[]; online: boolean }
export const siteVersions = (club: string) => rpc<SiteVersion[]>('site_versions_list', { p_club: club });
export const siteVersionDoc = (id: string) => rpc<SiteDoc | null>('site_version_doc', { p_id: id });
export const siteRestore = (id: string) => rpc<number>('site_restore', { p_id: id });
export const siteOptionRequest = (club: string) => rpc<null>('site_option_request', { p_club: club });
export const siteOptionSet = (club: string, active: boolean, until: string | null) => rpc<null>('site_option_set', { p_club: club, p_active: active, p_until: until });
/** La console de l'éditeur : le site d'un club et son option (null : pas de site). */
export const siteConsole = (club: string) => rpc<{ status: SiteRecord['status']; option_active: boolean; option_until: string | null; option_requested_at: string | null } | null>('site_console', { p_club: club });
export const siteShopSave = (club: string, shop: ShopLink[]) => rpc<null>('site_shop_save', { p_club: club, p_shop: shop });

// --- Les actualités -------------------------------------------------------------------------------------------------
export interface SiteNewsItem {
  id: string; title: string; body: string; media_id: string | null; team_ids: string[]; event_id: string | null; event: string | null;
  announced: boolean; by: string; published_at: string; teams: string[];
}
export const siteNewsList = async (club: string) => (await rpc<SiteNewsItem[] | null>('site_news_list', { p_club: club })) ?? [];
export const siteNewsSave = (club: string, id: string | null, n: { title: string; body: string; media: string | null; teams: string[]; event: string | null }, notify: boolean) =>
  rpc<string>('site_news_save', { p_club: club, p_id: id, p_title: n.title, p_body: n.body, p_media: n.media, p_teams: n.teams, p_event: n.event, p_notify: notify });
export const siteNewsDelete = (id: string) => rpc<null>('site_news_delete', { p_id: id });

// --- Les médias -----------------------------------------------------------------------------------------------------
export interface SiteMedia { id: string; kind: 'photo' | 'pdf'; name: string; mime: string; size: number; width: number | null; height: number | null; uploaded_at: string }
export async function siteMediaList(club: string): Promise<SiteMedia[]> {
  const { data, error } = await db().from('site_media').select('id, kind, name, mime, size, width, height, uploaded_at').eq('club_id', club).order('uploaded_at', { ascending: false }).limit(400);
  if (error) throw new Error(error.message);
  return (data ?? []) as SiteMedia[];
}
export async function siteMediaAdd(club: string, m: { kind: 'photo' | 'pdf'; name: string; mime: string; size: number; width?: number | null; height?: number | null; data: string; consent: boolean }): Promise<string> {
  const { data, error } = await db().from('site_media').insert({ club_id: club, kind: m.kind, name: m.name.slice(0, 120), mime: m.mime, size: m.size,
    width: m.width ?? null, height: m.height ?? null, data: m.data, consent: m.consent }).select('id').single();
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return (data as { id: string }).id;
}
export async function siteMediaDelete(id: string): Promise<void> {
  const { error } = await db().from('site_media').delete().eq('id', id);
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
}
/** Les images, lues une fois par visite : l'éditeur (le brouillon, bureau et fonction) ou le visiteur (le site publié). */
const cache = new Map<string, Promise<string | null>>();
export function mediaUrl(id: string, from: { club?: string; slug?: string }): Promise<string | null> {
  const key = `${from.slug ?? from.club}:${id}`;
  let p = cache.get(key);
  if (!p) {
    p = (from.slug
      ? rpc<{ mime: string; data: string } | null>('site_media_public', { p_slug: from.slug, p_id: id })
      : (async () => {
        const { data, error } = await db().from('site_media').select('mime, data').eq('id', id).maybeSingle();
        if (error) throw new Error(error.message);
        return data as { mime: string; data: string } | null;
      })()).then(m => (m ? `data:${m.mime};base64,${m.data}` : null)).catch(() => null);
    cache.set(key, p);
  }
  return p;
}
/** Une image qu'on vient d'envoyer : déjà connue, sans relire la base. */
export const rememberMedia = (id: string, club: string, url: string) => { cache.set(`${club}:${id}`, Promise.resolve(url)); };

// --- Le site publié (visiteur) ----------------------------------------------------------------------------------------
export interface PublicNews {
  id: string; title: string; body: string; media_id: string | null; published_at: string; teams: string[];
  event: { id: string; title: string; starts_at: string; location: string | null; signup: boolean } | null;
}
export interface PublicSite {
  doc: SiteDoc; n: number; published_at: string; color2: string | null; shop: ShopLink[]; news: PublicNews[];
  director: { name: string; title: string } | null;
}
export const sitePublic = (slug: string) => rpc<PublicSite | null>('site_public', { p_slug: slug });

// --- Les données des blocs vivants ------------------------------------------------------------------------------------
export interface SitePhoto { id: string; caption: string | null; match: string; date: string }
export interface SiteData {
  pub: PublicClub;
  agenda: PublicEvent[];
  photos: SitePhoto[];
  entries: MatchEntry[];
  bureau: { title: string; name: string }[];
  vgroups: { id: string; name: string; emoji: string; skill: string | null }[];
  /** D-181 : la boutique du club (les trois rayons, sans aucun nom), quand le module est activé. */
  shop?: ShopPublic | null;
}
/** Les blocs vivants, comme les lit le visiteur : la page du club en ligne. */
export async function liveDataBySlug(slug: string): Promise<SiteData | null> {
  const [pub, agenda, photos, entries, bureau, vgroups] = await Promise.all([clubPublic(slug), publicAgenda(slug).catch(() => [] as PublicEvent[]),
    clubPublicPhotos(slug).catch(() => [] as SitePhoto[]), clubMatchEntries({ slug }).catch(() => [] as MatchEntry[]),
    publicBureau(slug).catch(() => [] as { title: string; name: string }[]), clubPublicGroups(slug).catch(() => [])]);
  if (!pub) return null;
  const shop = await shopPublic(slug).catch(() => null);   // D-181
  return { pub, agenda: agenda ?? [], photos: (photos ?? []) as SitePhoto[], entries, bureau: bureau ?? [], vgroups, shop };
}
/** L'aperçu de l'éditeur : la page du club en ligne, les fonctions publiques ; pas encore en ligne, site_preview (les
 *  mêmes fonctions, lues sans rien mettre en ligne). */
export async function liveDataForEditor(club: string, slug: string | null, online: boolean): Promise<SiteData | null> {
  if (slug && online) return liveDataBySlug(slug);
  const p = await rpc<{ pub: PublicClub | null; agenda: PublicEvent[] | null; entries: MatchEntry[] | null; bureau: { title: string; name: string }[]; vgroups: SiteData['vgroups'] } | null>(
    'site_preview', { p_club: club }).catch(() => null);
  if (!p?.pub) return null;
  return { pub: p.pub, agenda: p.agenda ?? [], photos: [], entries: p.entries ?? [], bureau: p.bureau ?? [], vgroups: p.vgroups ?? [] };
}
